import API_CONFIG from '../config/api';
import { fetchAuthSession } from 'aws-amplify/auth';

/**
 * Get the current user's authentication token
 */
async function getAuthToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Failed to get auth token:', error);
    return undefined;
  }
}

/**
 * Generic API fetch wrapper with timeout and error handling
 */
async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_CONFIG.baseUrl}${endpoint}`;

  // Check if we're using a placeholder URL (local development)
  if (API_CONFIG.baseUrl.includes('your-api-gateway-url') || API_CONFIG.baseUrl === 'http://localhost:3000') {
    console.error('API not configured. Please set REACT_APP_API_BASE_URL environment variable.');
    throw new Error('API endpoint not configured. Please deploy the API or set REACT_APP_API_BASE_URL.');
  }

  // Get authentication token
  const authToken = await getAuthToken();
  if (!authToken) {
    throw new Error('Authentication required. Please log in.');
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`,
        ...options?.headers,
      },
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      // Handle 401 Unauthorized specifically
      if (response.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - please try again');
      }
      throw error;
    }
    throw new Error('An unexpected error occurred');
  }
}

export interface ConfigItem {
  key: string;
  value: string;
  status: 'ACTIVE' | 'PENDING' | 'UNKNOWN';
}

export interface ProductConfigItem extends ConfigItem {
  productId: string;
}

export interface ConfigResponse {
  success: boolean;
  configs?: ConfigItem[] | ProductConfigItem[];
  error?: string;
}

export interface UpsertConfigResponse {
  success: boolean;
  message?: string;
  key?: string;
  value?: string;
  productId?: string;
  status?: string;
  error?: string;
}

export interface DeleteConfigResponse {
  success: boolean;
  message?: string;
  key?: string;
  productId?: string;
  error?: string;
}

/**
 * Fetch all global configurations
 */
export async function fetchGlobalConfigs(): Promise<ConfigItem[]> {
  const response = await apiFetch<ConfigResponse>('/v1/config/global', {
    method: 'GET',
  });
  
  if (!response.success || !response.configs) {
    throw new Error(response.error || 'Failed to fetch global configs');
  }
  
  return response.configs as ConfigItem[];
}

/**
 * Fetch all product configurations
 */
export async function fetchAllProductConfigs(): Promise<ProductConfigItem[]> {
  const response = await apiFetch<ConfigResponse>('/v1/config/product', {
    method: 'GET',
  });
  
  if (!response.success || !response.configs) {
    throw new Error(response.error || 'Failed to fetch product configs');
  }
  
  return response.configs as ProductConfigItem[];
}

/**
 * Fetch configurations for a specific product
 */
export async function fetchProductConfigs(productId: string): Promise<ProductConfigItem[]> {
  const response = await apiFetch<ConfigResponse>(`/v1/config/product/${encodeURIComponent(productId)}`, {
    method: 'GET',
  });
  
  if (!response.success || !response.configs) {
    throw new Error(response.error || 'Failed to fetch product configs');
  }
  
  return response.configs as ProductConfigItem[];
}

/**
 * Create or update a global configuration
 */
export async function upsertGlobalConfig(key: string, value: string): Promise<UpsertConfigResponse> {
  const response = await apiFetch<UpsertConfigResponse>('/v1/config/global', {
    method: 'POST',
    body: JSON.stringify({ key, value }),
  });
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to upsert global config');
  }
  
  return response;
}

/**
 * Create or update a product configuration
 */
export async function upsertProductConfig(productId: string, key: string, value: string): Promise<UpsertConfigResponse> {
  const response = await apiFetch<UpsertConfigResponse>('/v1/config/product', {
    method: 'POST',
    body: JSON.stringify({ productId, key, value }),
  });
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to upsert product config');
  }
  
  return response;
}

/**
 * Delete a global configuration
 */
export async function deleteGlobalConfig(key: string): Promise<DeleteConfigResponse> {
  const response = await apiFetch<DeleteConfigResponse>(`/v1/config/global/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to delete global config');
  }
  
  return response;
}

/**
 * Delete a product configuration
 */
export async function deleteProductConfig(productId: string, key: string): Promise<DeleteConfigResponse> {
  const response = await apiFetch<DeleteConfigResponse>(`/v1/config/product/${encodeURIComponent(productId)}/${encodeURIComponent(key)}`, {
    method: 'DELETE',
  });
  
  if (!response.success) {
    throw new Error(response.error || 'Failed to delete product config');
  }
  
  return response;
}
